import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, HelpCircle, FileText, MessageSquare, FolderOpen, 
  CheckSquare, Calendar, BookOpen, Bell, User, ChevronDown, ChevronUp,
  Search, Phone, Clock, Shield, Settings, Loader2, Scale
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Icon mapping for dynamic icons from database
const iconMap = {
  User, FileText, MessageSquare, FolderOpen, CheckSquare, 
  Calendar, BookOpen, Bell, Shield, HelpCircle, Settings
};

// Feature Guide Data (static - these are quick start guides, not FAQs)
const featureGuides = [
  {
    title: 'Dashboard',
    icon: FileText,
    color: 'bg-purple-100 text-[#80298F]',
    description: 'Your central hub for case overview',
    steps: [
      'View all your active cases and their current status',
      'See pending tasks that need your attention',
      'Check unread messages from your legal team',
      'View upcoming appointments and deadlines'
    ]
  },
  {
    title: 'Messages',
    icon: MessageSquare,
    color: 'bg-green-100 text-green-600',
    description: 'Secure communication with your legal team',
    steps: [
      'Click "Messages" in the navigation bar',
      'Select a case to view related messages',
      'Click "New Message" to start a conversation',
      'Attach files if needed and send your message'
    ]
  },
  {
    title: 'Documents',
    icon: FolderOpen,
    color: 'bg-amber-100 text-amber-600',
    description: 'Access and manage case documents',
    steps: [
      'Navigate to the "Documents" section',
      'Browse documents by case',
      'Click "Upload" to add new documents',
      'Click "Download" to save documents to your device'
    ]
  },
  {
    title: 'Calendar',
    icon: Calendar,
    color: 'bg-indigo-100 text-indigo-600',
    description: 'Track appointments and deadlines',
    steps: [
      'View the calendar from the navigation menu',
      'Navigate between months using arrows',
      'Click on dates to see scheduled events',
      'Filter by case to see specific appointments'
    ]
  },
  {
    title: 'Resources',
    icon: BookOpen,
    color: 'bg-teal-100 text-teal-600',
    description: 'Educational materials and guides',
    steps: [
      'Access Resources from the navigation',
      'Browse by category or use search',
      'Filter by document type or video',
      'Download or view materials online'
    ]
  },
  {
    title: 'Notifications',
    icon: Settings,
    color: 'bg-pink-100 text-pink-600',
    description: 'Customize how you receive updates',
    steps: [
      'Click your name in the top-right corner',
      'Select "Notification Settings"',
      'Toggle Email, SMS, or Push notifications',
      'Changes save automatically'
    ]
  }
];

// Fallback glossary data (used if backend returns empty)
const fallbackGlossary = [
  { term: 'Answer', definition: "The defendant's formal written response to your complaint, filed with the court. The answer admits or denies each allegation in the complaint and may raise defenses or counterclaims." },
  { term: 'Appeal', definition: 'A request to a higher court to review and change the decision made by a lower court. Appeals focus on legal errors, not re-trying the facts of the case.' },
  { term: 'Claim', definition: 'A legal assertion that you have been harmed or suffered damages and are entitled to compensation or other relief from the defendant.' },
  { term: 'Complaint', definition: 'The initial legal document filed with the court that starts a lawsuit. It outlines your claims, the facts supporting them, and the relief you are seeking.' },
  { term: 'Contingency Fee', definition: 'A fee arrangement where the attorney only gets paid if you win your case. The fee is a percentage of your recovery, so you pay nothing upfront or out of pocket.' },
  { term: 'Damages', definition: 'The compensation you seek for harm suffered, including medical expenses, lost wages, pain and suffering, emotional distress, and other losses.' },
  { term: 'Defendant', definition: 'The person, company, or entity being sued. In your case, this is the party you claim caused your injuries or violated your rights.' },
  { term: 'Deposition', definition: 'A formal proceeding where a witness or party gives sworn testimony outside of court, with a court reporter present. The testimony is recorded and can be used at trial.' },
  { term: 'Discovery', definition: 'The pre-trial process where both sides exchange information and evidence. This includes written questions, document requests, and depositions.' },
  { term: 'Settlement', definition: 'An agreement between parties to resolve the case without going to trial. The defendant agrees to pay a specific amount, and you agree to release your claims.' },
  { term: 'Trial', definition: 'A formal court proceeding where both sides present evidence and arguments to a judge or jury, who then decides the outcome of the case.' },
  { term: 'Verdict', definition: 'The formal decision made by a judge or jury at the end of a trial, determining who wins the case and what damages, if any, are awarded.' }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors px-2 rounded"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 px-2">
          <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function GlossaryItem({ term, definition }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors px-2 rounded"
      >
        <span className="font-semibold text-[#80298F] pr-4">{term}</span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-3 px-2">
          <p className="text-gray-600 text-sm leading-relaxed">{definition}</p>
        </div>
      )}
    </div>
  );
}

function Help({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [glossaryData, setGlossaryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
    fetchGlossary();
  }, []);

  // Handle hash navigation to scroll to specific sections
  useEffect(() => {
    if (location.hash && !loading) {
      const elementId = location.hash.replace('#', '');
      const element = document.getElementById(elementId);
      if (element) {
        // Small delay to ensure the element is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash, loading]);

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`${API}/faqs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFaqData(data);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGlossary = async () => {
    try {
      const response = await fetch(`${API}/glossary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGlossaryData(data.length > 0 ? data : fallbackGlossary);
      } else {
        setGlossaryData(fallbackGlossary);
      }
    } catch (error) {
      console.error('Error fetching glossary:', error);
      setGlossaryData(fallbackGlossary);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  // Get icon component from string name
  const getIconComponent = (iconName) => {
    return iconMap[iconName] || HelpCircle;
  };

  // Filter FAQs based on search
  const filteredFaqs = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;

  // Filter glossary terms based on search
  const filteredGlossary = searchQuery
    ? glossaryData.filter(
        item => item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.definition.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : glossaryData;

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-[#80298F]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="text-center mb-10">
          <div className="inline-flex p-3 bg-purple-100 rounded-2xl mb-4">
            <HelpCircle className="h-10 w-10 text-[#80298F]" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions and learn how to make the most of your client portal.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-3 text-base"
            />
          </div>
        </div>

        {/* Quick Start Guides */}
        {!searchQuery && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Quick Start Guides</h2>
              {user?.role === 'client' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Set flag to force show tour on dashboard
                    localStorage.setItem(`show_tour_${user.user_id}`, 'true');
                    navigate('/dashboard');
                  }}
                  className="text-[#80298F] border-[#80298F] hover:bg-[#80298F] hover:text-white"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Take Portal Tour
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featureGuides.map((guide, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${guide.color}`}>
                        <guide.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{guide.title}</CardTitle>
                        <p className="text-xs text-gray-500">{guide.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2">
                      {guide.steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-500 flex-shrink-0 mt-0.5">
                            {stepIndex + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {searchQuery ? `Search Results for "${searchQuery}"` : 'Frequently Asked Questions'}
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
            </div>
          ) : filteredFaqs.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-xl">
              <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              {searchQuery ? (
                <>
                  <p className="text-gray-500">No results found for &quot;{searchQuery}&quot;</p>
                  <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                </>
              ) : (
                <>
                  <p className="text-gray-500">No FAQs available yet</p>
                  <p className="text-sm text-gray-400 mt-1">Check back later for helpful information</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((category, catIndex) => {
                const IconComponent = getIconComponent(category.icon);
                return (
                  <Card key={category.category_id || catIndex}>
                    <button
                      onClick={() => setActiveCategory(activeCategory === catIndex ? null : catIndex)}
                      className="w-full"
                    >
                      <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${category.color}`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div className="text-left">
                              <CardTitle className="text-base">{category.name}</CardTitle>
                              <p className="text-xs text-gray-500">{category.questions.length} questions</p>
                            </div>
                          </div>
                          {activeCategory === catIndex ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                    </button>
                    {(activeCategory === catIndex || searchQuery) && (
                      <CardContent className="pt-0">
                        {category.questions.map((faq, faqIndex) => (
                          <FAQItem key={faqIndex} question={faq.question} answer={faq.answer} />
                        ))}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Legal Terms Glossary */}
        {(filteredGlossary.length > 0 || !searchQuery) && (
          <section className="mb-12" id="glossary">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Scale className="h-6 w-6 text-[#80298F]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {searchQuery ? `Legal Terms Matching "${searchQuery}"` : 'Legal Terms Glossary'}
                </h2>
                <p className="text-sm text-gray-500">
                  {searchQuery 
                    ? `${filteredGlossary.length} term${filteredGlossary.length !== 1 ? 's' : ''} found`
                    : 'Common legal terms explained in plain language'
                  }
                </p>
              </div>
            </div>
            
            <Card>
              <CardContent className="py-4">
                {filteredGlossary.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No glossary terms available.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    {filteredGlossary.map((item, index) => (
                      <GlossaryItem key={item.term_id || index} term={item.term} definition={item.definition} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        )}

        {/* Contact Support */}
        <section className="mb-8">
          <Card className="bg-gradient-to-r from-[#80298F] to-[#9B3BA7] text-white">
            <CardContent className="py-8">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Still Need Help?</h3>
                <p className="text-white/80 mb-6 max-w-md mx-auto">
                  Our team is here to assist you.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    variant="secondary"
                    className="bg-white text-[#80298F] hover:bg-gray-100"
                    onClick={() => navigate('/messages')}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send a Message
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    onClick={() => window.location.href = 'tel:+18004110428'}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Us
                  </Button>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Mon-Fri: 7:30 a.m. - 5:00 p.m.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}

export default Help;
